import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form';
import { Alert, Dialog, DialogActions, DialogContent, Grid, Link } from '@mui/material';
import AppSelectList, { IDropDownOptions } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { createScorecardRequest } from '../../../../Component/Model/Dto/WSP/SETAScorecardLicenses';
import { defaultObject } from '../../../../Helpers/typescriptHelper';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import { CreateSetaScorecardValidationSchema } from './CreateSetaScorecardValidationSchema';
import { WSPScorecardService } from '../../../../Component/Api/WSPScorecard/WSPScorecardService';
import { toast } from 'react-toastify';
import { OrganisationService } from '../../../../Component/Api/Organisation/OrganisationService';
import { TooltipLoadingButton } from '../../../../Component/Common/Components/Tooltip/TooltipLoadingButton';
import { SmallLoading } from '../../../../Component/Common/Components/Loading/IntermediateLoading';
import { Organisation } from '../../../../Component/Model/Dto/Organisation/Organisation';
import { SETATypesService } from '../../../../Component/Api/SETA/SETATypesService';
import { getIOptionsArrayWithInfo } from '../../../../Helpers/DropdownWithDescriptionHelper';
import AppSelectListWithInfo, { IDropDownOptionsWithDescription } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectListWithInfo';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';

interface IProps {
    open: boolean;
    closeModal: () => void
}
export const CreateSetaScorecard = ({ open, closeModal }: IProps) => {
    const [organisationDataReq, setOrganisationDataReq] = useState<Organisation[]>(defaultObject);
    const [filteredReportingPeriods, setFilteredReportingPeriods] = useState<IDropDownOptions[]>([]);
    const [nextScorecardLicenseId, setNextScorecardLicenseId] = useState<string>("");
    const [isLicenseNotAvailable, setIsLicenseNotAvailable] = useState<boolean>(false);
    const [allLicensesDepleted, setAllLicensesDepleted] = useState<boolean>(false);
    const [hasOrganisation, setHasOrganisation] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [reportingPeriodDisabled, setReportingPeriodDisabled] = useState<boolean>(true);
    const [setaListItems, setSETAListItems] = useState<IDropDownOptionsWithDescription[]>([]);

    const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(CreateSetaScorecardValidationSchema)
    });

    const handleOrganisationChange = async (organisationId: number) => {
        const wspScorcardData = await WSPScorecardService.getWSPScorecardLicenses()
        const licenses = wspScorcardData.data;
        const selectedOrganisation = organisationDataReq.find(org => org.organisationId === organisationId)
        const selectedScorecard = licenses.find(data => data.setaTypeId === selectedOrganisation?.setaTypeId);

        if (selectedScorecard !== undefined) {
            setIsLicenseNotAvailable(false);
            setReportingPeriodDisabled(true);
            if (selectedScorecard.numberOfAvailableScorecards != 0) {
                setAllLicensesDepleted(false);
                setReportingPeriodDisabled(false);
                const updatedReportingPeriods = selectedScorecard.reportingPeriods.map(period => ({
                    id: period.id,
                    value: period.reportingPeriod
                }));
                setFilteredReportingPeriods(updatedReportingPeriods);
                setNextScorecardLicenseId(selectedScorecard.nextScorecardLicenseId);
            } else {
                setAllLicensesDepleted(true);
                setReportingPeriodDisabled(true);
            }

        } else {
            setIsLicenseNotAvailable(true);
        }

    };

    useEffect(() => {
        if (open) {
            fetchAndSetFormData();
        }
    }, [open]);

    const fetchAndSetFormData = async () => {
        reset()
        try {
            const organisations = await OrganisationService.getAllOrganisations();
            setReportingPeriodDisabled(true);
            if (organisations.data.length != 0) {
                setHasOrganisation(true);
                setOrganisationDataReq(organisations.data);

                const listItems: any[] = [];

                const SETATypes = await SETATypesService.getAll();

                organisations.data.forEach((element) => {
                    const listItem = SETATypes.data.find(data => data.id == element.setaTypeId);
                    if (listItem) {
                        listItems.push({
                            OrganisationId: element.organisationId,
                            OrganisationName: element.registrationName,
                            SETATypeDescription: `Associated to ${listItem.type}`
                        })
                    } else {
                        listItems.push({
                            OrganisationId: element.organisationId,
                            OrganisationName: element.registrationName,
                            SETATypeDescription: `Not associated to any SETA`
                        })
                    }
                });

                const organisationOptions = getIOptionsArrayWithInfo(
                    listItems,
                    "OrganisationId",
                    "OrganisationName",
                    "SETATypeDescription"
                );

                setSETAListItems(organisationOptions);
            }
            setIsLoading(true)
        } catch (error) {
            console.error("Error fetching scorecard data:", error);
        } finally {
            setIsLoading(false)
        }
    };

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const finalData: createScorecardRequest = {
                organisationId: data.organisationsId,
                scorecardLicenseId: nextScorecardLicenseId,
                reportingPeriod: data.reportingPeriod,
                scorecardName: data.scorecardName
            };
            await WSPScorecardService.createScorecard(finalData);
            toast.success(`Scorecard created: ${data.scorecardName}`)
        } catch (error) {
            toast.error(`Could not create Scorecard`);
        } finally {
            closeModal()
        }
    }

    function getTooltipTitle() {
        if (!hasOrganisation) {
            return "Creating a scorecard requires an organisation";
        } else if (isLicenseNotAvailable) {
            return "Creating a scorecard requires a SETA";
        } else if (allLicensesDepleted) {
            return "No availabe scorecard licenses";
        } else {
            return "";
        }
    }

    return (
        <Dialog open={open} onClose={() => closeModal()} maxWidth={false} aria-labelledby="form-dialog-title">
            <CustomDialogTitle onClose={() => closeModal()}>
                Create Scorecard
            </CustomDialogTitle>
            <DialogContent sx={{ paddingTop: "5px !important", width: "600px " }}>
                {isLicenseNotAvailable ?
                    <>
                        <form>
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <AppSelectListWithInfo
                                        control={control}
                                        name="organisationsId"
                                        label="Organisations"
                                        items={setaListItems}
                                        disabled={setaListItems.length === 0}
                                        onChange={handleOrganisationChange}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Alert severity='warning'>This organisation is not associated with any SETA. Please contact your administrator to update the organisation.</Alert>
                                </Grid>
                            </Grid>
                        </form>
                    </>
                    :
                    <>
                        {allLicensesDepleted ?
                            <>
                                <form>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Alert severity='warning'>No licenses available. Please  contact <Link href="mailto:support@BEE123.co.za">support@BEE123.co.za</Link> for additional licenses.</Alert>
                                        </Grid>
                                    </Grid>
                                </form>
                            </>
                            :
                            <>
                                <form id='SETAScorecardForm' onSubmit={handleSubmit(handleDataSubmit)}>
                                    {isLoading ? <SmallLoading></SmallLoading> :
                                        <>
                                            {hasOrganisation ?
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12}>
                                                        <AppSelectListWithInfo
                                                            control={control}
                                                            name="organisationsId"
                                                            label="Organisations"
                                                            items={setaListItems}
                                                            disabled={setaListItems.length === 0}
                                                            onChange={handleOrganisationChange}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <AppSelectList
                                                            control={control}
                                                            name="reportingPeriod"
                                                            label="Reporting Period"
                                                            items={filteredReportingPeriods}
                                                            size="small"
                                                            disabled={reportingPeriodDisabled}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <AppTextInput label='Scorecard Name' name='scorecardName' control={control} />
                                                    </Grid>
                                                </Grid>
                                                :
                                                <Alert severity="warning" style={{ paddingTop: 5 }}>
                                                    It is required to choose an organisation when creating a scorecard.
                                                    <br></br>You have not been granted access to any organisations. <br></br>Please contact your administrator to request access.
                                                </Alert>}
                                        </>}
                                </form>
                            </>
                        }

                    </>
                }
            </DialogContent>
            <DialogActions>
                <TooltipLoadingButton
                    marginRight="10"
                    buttonText="Create"
                    form="SETAScorecardForm"
                    tooltipTitle={getTooltipTitle()}
                    disabled={!hasOrganisation || isLicenseNotAvailable || allLicensesDepleted}
                    isLoading={isSubmitting}
                    showTooltipOnDisabled={true} />
                <MDButton variant='outlined' color='primary' onClick={() => closeModal()}>Cancel</MDButton>
            </DialogActions>
        </Dialog>

    )
}

